import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, IconButton, Button,
  TextField, Tabs, Tab, CircularProgress, Alert, Divider,
  Chip, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

// Inbyggda standardvärden (samma som i IUP.js)
const DEFAULT_MEDARBETARE = {
  criteria: [
    { key: 'kompetens', label: 'Kompetens', desc: 'Kunskap, färdighet, erfarenhet, beslutsförmåga och förmågan att göra rätt.', builtin: true },
    { key: 'kreativitet', label: 'Kreativitet', desc: 'Förmågan att finna nya lösningar.', builtin: true },
    { key: 'socialKompetens', label: 'Social kompetens', desc: 'Viljan och förmågan att hjälpa arbetskamrater, visa god kamratskap, relationer med ledare och arbetskamrater', builtin: true },
    { key: 'hangivenhet', label: 'Hängivenhet', desc: 'Förmågan till engagemang för arbetsuppgifter och företaget, hög moral och vilja till hårt arbete i svåra situationer', builtin: true },
    { key: 'enkelt', label: 'Enkelt', desc: 'Förmågan att arbeta efter enkelhet', builtin: true },
    { key: 'affarsmassighet', label: 'Affärsmässighet', desc: 'Förmågan att genomföra arbetsuppgifterna kostnadseffektivt till ökad kundnytta och kundnöjdhet', builtin: true },
  ],
  ratingOptions: ['Enligt förväntan', 'Över förväntan', 'Långt över förväntan'],
};

const DEFAULT_GRUPPLEDARE = {
  questions: [
    { key: 'q1', label: '1. Min nuvarande roll på Tikab vill jag beskriva så här (arbetsuppgifter, utmaning, arbetsbörda och trivsel):', builtin: true },
    { key: 'q2', label: '2. Min önskade roll på Tikab vill jag beskriva så här (arbetsuppgifter, utmaning, arbetsbörda och trivsel):', builtin: true },
    { key: 'q3', label: '3. Jag upplever min ledande roll på Tikab på följande sätt:', builtin: true },
    { key: 'q4', label: '4. Saker jag lärt mig/utvecklat under året:', builtin: true },
    { key: 'q5', label: '5. För att fortsätta utvecklas (både som medarbetare och ledare) behöver jag bibehålla (egenskaper och kompetenser):', builtin: true },
    { key: 'q6', label: '6. För att fortsätta utvecklas (både som medarbetare och ledare) behöver jag förstärka och utveckla (egenskaper och kompetenser):', builtin: true },
  ],
  q7Label: '7. Något som jag hamnat i senaste veckan som jag upplevt som stimulande och positivt:',
  q8Label: '8. Något som jag hamnat i senaste veckan som jag upplevt som ostimulerande och negativt:',
  q9Label: '9. Det här skulle jag vilja att Tikab gör mer av:',
};

const DEFAULT_AO = {
  questions: [
    { key: 'q1', label: '1. Min nuvarande roll som Affärsområdesansvarig vill jag beskriva så här (affärsområdets status, resultat, utmaningar och trivsel):', builtin: true },
    { key: 'q2', label: '2. Min önskade utveckling av affärsområdet vill jag beskriva så här (strategiska mål, förändringar, tillväxt):', builtin: true },
    { key: 'q3', label: '3. Jag upplever min strategiska och operativa ledarroll på Tikab på följande sätt:', builtin: true },
    { key: 'q4', label: '4. Saker jag lärt mig/utvecklat som ledare och affärsområdesansvarig under året:', builtin: true },
    { key: 'q5', label: '5. För att fortsätta utvecklas som affärsområdesansvarig behöver jag bibehålla (egenskaper och kompetenser):', builtin: true },
    { key: 'q6', label: '6. För att fortsätta utvecklas behöver jag förstärka och utveckla (egenskaper och kompetenser):', builtin: true },
  ],
  q7Label: '7. Något som gått särskilt bra under perioden (affärsmässigt, ledarskap, kundrelationer):',
  q8Label: '8. Något som jag upplevt som utmanande eller negativt under perioden:',
  q9Label: '9. Det här skulle jag vilja att Tikab gör mer av på organisationsnivå:',
};

// ── Kriterieredigerare (Medarbetare) ──────────────────────────────
function CriteriaEditor({ criteria, onChange }) {
  function updateCriterion(idx, field, value) {
    const updated = criteria.map((c, i) => i === idx ? { ...c, [field]: value } : c);
    onChange(updated);
  }

  function addCriterion() {
    onChange([...criteria, {
      key: `extra_${Date.now()}`,
      label: 'Nytt kriterium',
      desc: '',
      builtin: false,
    }]);
  }

  function removeCriterion(idx) {
    onChange(criteria.filter((_, i) => i !== idx));
  }

  return (
    <Box>
      {criteria.map((c, idx) => (
        <Paper key={c.key} sx={{ p: 2.5, mb: 2,
          borderLeft: `4px solid ${c.builtin ? '#FF6B00' : '#2196f3'}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', mb: 1 }}>
            <Chip
              size="small"
              label={c.builtin ? 'Inbyggd' : 'Tillagd'}
              color={c.builtin ? 'default' : 'primary'}
              sx={{ fontSize: '0.65rem' }}
            />
            {!c.builtin && (
              <Tooltip title="Ta bort">
                <IconButton size="small" color="error"
                  onClick={() => removeCriterion(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <TextField
            fullWidth
            size="small"
            label="Titel"
            value={c.label}
            onChange={e => updateCriterion(idx, 'label', e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Beskrivning (visas kursivt i formuläret)"
            value={c.desc || ''}
            onChange={e => updateCriterion(idx, 'desc', e.target.value)}
          />
        </Paper>
      ))}
      <Button
        startIcon={<AddIcon />}
        onClick={addCriterion}
        variant="outlined"
        size="small"
        sx={{ mt: 1 }}
      >
        Lägg till kriterium
      </Button>
    </Box>
  );
}

// ── Frågeredigerare (Ledar-IUP) ───────────────────────────────────
function QuestionsEditor({ questions, q7Label, q8Label, q9Label, onChange, onLabelChange }) {
  function updateQuestion(idx, value) {
    const updated = questions.map((q, i) => i === idx ? { ...q, label: value } : q);
    onChange(updated);
  }

  function addQuestion() {
    const nextN = questions.filter(q => !q.builtin).length + 1;
    onChange([...questions, {
      key: `extra_${Date.now()}`,
      label: `Tilläggsfråga ${nextN}:`,
      builtin: false,
    }]);
  }

  function removeQuestion(idx) {
    onChange(questions.filter((_, i) => i !== idx));
  }

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
        Frågor 1–{questions.length}
      </Typography>
      {questions.map((q, idx) => (
        <Paper key={q.key} sx={{ p: 2, mb: 1.5,
          borderLeft: `4px solid ${q.builtin ? '#FF6B00' : '#2196f3'}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              size="small"
              label={q.builtin ? 'Inbyggd' : 'Tillagd'}
              color={q.builtin ? 'default' : 'primary'}
              sx={{ fontSize: '0.65rem' }}
            />
            {!q.builtin && (
              <Tooltip title="Ta bort">
                <IconButton size="small" color="error"
                  onClick={() => removeQuestion(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Frågetext"
            value={q.label}
            onChange={e => updateQuestion(idx, e.target.value)}
          />
        </Paper>
      ))}
      <Button
        startIcon={<AddIcon />}
        onClick={addQuestion}
        variant="outlined"
        size="small"
        sx={{ mt: 1, mb: 3 }}
      >
        Lägg till fråga
      </Button>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
        Sammanfattningsfält (fast struktur, bara text redigerbar)
      </Typography>
      {[
        { id: 'q7Label', label: 'Rubrik för Q7 (3 positiva)', value: q7Label },
        { id: 'q8Label', label: 'Rubrik för Q8 (3 negativa)', value: q8Label },
        { id: 'q9Label', label: 'Rubrik för Q9 (fritext)', value: q9Label },
      ].map(f => (
        <TextField
          key={f.id}
          fullWidth
          size="small"
          multiline
          minRows={2}
          label={f.label}
          value={f.value}
          onChange={e => onLabelChange(f.id, e.target.value)}
          sx={{ mb: 2 }}
        />
      ))}
    </Box>
  );
}

// ── Huvudexport ───────────────────────────────────────────────────
export default function IUPTemplateEditor() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [canAccess, setCanAccess] = useState(false);

  const [medarbetare, setMedarbetare] = useState(null);
  const [gruppledare, setGruppledare] = useState(null);
  const [aoAnsvarig, setAoAnsvarig] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userSnap.exists() ? userSnap.data() : {};
      const isHR = userData.isHRansvarig;
      const isSuperOrVD = userData.role === 'superadmin' || userData.role === 'vd';
      if (!isHR && !isSuperOrVD) {
        setError('Åtkomst nekad. Kräver HR-ansvarig, Superadmin eller VD.');
        setLoading(false);
        return;
      }
      setCanAccess(true);

      const [medSnap, glSnap, aoSnap] = await Promise.all([
        getDoc(doc(db, 'iupTemplates', 'medarbetare')),
        getDoc(doc(db, 'iupTemplates', 'gruppledare')),
        getDoc(doc(db, 'iupTemplates', 'ao_ansvarig')),
      ]);
      setMedarbetare(medSnap.exists() ? medSnap.data() : { ...DEFAULT_MEDARBETARE });
      setGruppledare(glSnap.exists() ? glSnap.data() : { ...DEFAULT_GRUPPLEDARE });
      setAoAnsvarig(aoSnap.exists() ? aoSnap.data() : { ...DEFAULT_AO });
      setLoading(false);
    }
    load();
  }, [currentUser]);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await Promise.all([
        setDoc(doc(db, 'iupTemplates', 'medarbetare'), medarbetare),
        setDoc(doc(db, 'iupTemplates', 'gruppledare'), gruppledare),
        setDoc(doc(db, 'iupTemplates', 'ao_ansvarig'), aoAnsvarig),
      ]);
      setSaved('Mallar sparade!');
      setTimeout(() => setSaved(''), 3000);
    } catch (err) {
      setError('Sparning misslyckades: ' + err.message);
    }
    setSaving(false);
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (!canAccess) return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Alert severity="error">{error}</Alert>
    </Container>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            IUP-mallar
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Redigera frågor och kriterier för alla IUP-typer
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          onClick={handleSave}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65c00' } }}
        >
          {saving ? 'Sparar...' : 'Spara alla mallar'}
        </Button>
      </Box>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}

        <Alert severity="info" sx={{ mb: 3 }}>
          Ändringar i mallar påverkar <strong>nya</strong> IUP-formulär. Inbyggda frågor kan
          redigeras men inte tas bort. Egna tilläggsfrågor kan läggas till och raderas.
        </Alert>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Medarbetare-IUP" />
          <Tab label="Gruppledare-IUP" />
          <Tab label="AO-ansvarig-IUP" />
        </Tabs>

        {tab === 0 && medarbetare && (
          <CriteriaEditor
            criteria={medarbetare.criteria || DEFAULT_MEDARBETARE.criteria}
            onChange={criteria => setMedarbetare(prev => ({ ...prev, criteria }))}
          />
        )}

        {tab === 1 && gruppledare && (
          <QuestionsEditor
            questions={gruppledare.questions || DEFAULT_GRUPPLEDARE.questions}
            q7Label={gruppledare.q7Label || DEFAULT_GRUPPLEDARE.q7Label}
            q8Label={gruppledare.q8Label || DEFAULT_GRUPPLEDARE.q8Label}
            q9Label={gruppledare.q9Label || DEFAULT_GRUPPLEDARE.q9Label}
            onChange={questions => setGruppledare(prev => ({ ...prev, questions }))}
            onLabelChange={(field, value) =>
              setGruppledare(prev => ({ ...prev, [field]: value }))
            }
          />
        )}

        {tab === 2 && aoAnsvarig && (
          <QuestionsEditor
            questions={aoAnsvarig.questions || DEFAULT_AO.questions}
            q7Label={aoAnsvarig.q7Label || DEFAULT_AO.q7Label}
            q8Label={aoAnsvarig.q8Label || DEFAULT_AO.q8Label}
            q9Label={aoAnsvarig.q9Label || DEFAULT_AO.q9Label}
            onChange={questions => setAoAnsvarig(prev => ({ ...prev, questions }))}
            onLabelChange={(field, value) =>
              setAoAnsvarig(prev => ({ ...prev, [field]: value }))
            }
          />
        )}
      </Container>
    </Box>
  );
}
